import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, UserPlus } from "lucide-react";
import { createClient } from "@supabase/supabase-js";

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY;

const supabase =
  SUPABASE_URL && SUPABASE_ANON_KEY
    ? createClient(SUPABASE_URL, SUPABASE_ANON_KEY)
    : null;

export default function Signup() {
  const [form, setForm] = useState({
    email: "",
    password: "",
    username: "",
    displayName: "",
    discord: "",
    twitchLogin: "",
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  const update = (e) => {
    setForm((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handleSignup = async (e) => {
    e.preventDefault();

    setLoading(true);
    setError("");
    setMessage("");

    try {
      if (!supabase) {
        throw new Error("Supabase is not configured properly.");
      }

      const { data, error } = await supabase.auth.signUp({
        email: form.email.trim(),
        password: form.password,
        options: {
          data: {
            username: form.username.trim(),
            display_name: form.displayName.trim(),
            discord: form.discord.trim(),
            twitch_login: form.twitchLogin.trim(),
          },
        },
      });

      if (error) {
        throw error;
      }

      setMessage(
        "Account created! Check your email to confirm your signup."
      );

      setForm({
        email: "",
        password: "",
        username: "",
        displayName: "",
        discord: "",
        twitchLogin: "",
      });

      console.log("Signup success:", data);
    } catch (err) {
      setError(err.message || "Signup failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="pt-28 md:pt-36 min-h-screen" data-testid="signup-page">
      <section className="border-b border-white/10">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
          <p className="overline text-gold">▸ Join The Network</p>

          <h1 className="text-[14vw] md:text-[7rem] text-white mt-5">
            Create <br />
            <span className="text-gold">Account</span>
          </h1>

          <p className="mt-6 text-white/70 max-w-xl mx-auto">
            Start as a recruit. Staff approval unlocks deeper access.
          </p>
        </div>
      </section>

      <section className="py-16">
        <form
          onSubmit={handleSignup}
          className="max-w-xl mx-auto space-y-5 p-8 border border-white/10 rounded-lg"
        >
          <div className="flex items-center gap-2 text-gold">
            <UserPlus />
            <span>Signup</span>
          </div>

          <input
            name="email"
            type="email"
            placeholder="Email"
            value={form.email}
            onChange={update}
            required
            className="w-full p-3 bg-black border border-white/20"
          />

          <input
            name="password"
            type="password"
            placeholder="Password"
            value={form.password}
            onChange={update}
            required
            minLength={6}
            className="w-full p-3 bg-black border border-white/20"
          />

          <input
            name="username"
            placeholder="Username"
            value={form.username}
            onChange={update}
            required
            className="w-full p-3 bg-black border border-white/20"
          />

          <input
            name="displayName"
            placeholder="Display Name"
            value={form.displayName}
            onChange={update}
            required
            className="w-full p-3 bg-black border border-white/20"
          />

          <input
            name="discord"
            placeholder="Discord (optional)"
            value={form.discord}
            onChange={update}
            className="w-full p-3 bg-black border border-white/20"
          />

          <input
            name="twitchLogin"
            placeholder="Twitch Username (optional)"
            value={form.twitchLogin}
            onChange={update}
            className="w-full p-3 bg-black border border-white/20"
          />

          {error && (
            <p className="text-red-400 text-sm">{error}</p>
          )}

          {message && (
            <p className="text-green-400 text-sm">{message}</p>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full p-3 bg-gold text-black flex items-center justify-center gap-2 disabled:opacity-50"
          >
            {loading ? "Creating..." : "Create Account"}
            <ArrowRight size={16} />
          </button>

          <p className="text-sm text-white/60 text-center">
            Already have an account?{" "}
            <Link to="/login" className="text-gold">
              Log in
            </Link>
          </p>
        </form>
      </section>
    </div>
  );
}